import React from 'react';
import { useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';
import { ENV_ENDPOINTS } from 'config';
import { observer } from 'mobx-react';

import { UIDAPresentationTypeEnum } from '@kaltura-ott/tvpil';
import { appStore, PageStore, ParentalPinType, SubpageStore } from '@kaltura-ott/tvpil-shared';

import { o2Domains } from 'components/widgets/Login/constants';
import DefaultModal from 'components/widgets/Modal/DefaultModal/DefaultModal';
import PinCodeModal from 'components/widgets/Modal/PinCodeModal/PinCodeModal';
import { PinCodeModalTypes } from 'components/widgets/Modal/PinCodeModal/PinCodeModalTypesEnum';
import { DO_NOT_SHOW_AGAIN, FAVORITES, IMAGE_PARAM } from 'consts';
import { useModalContext } from 'context/modal/modal';
import { useStableReactRouter } from 'context/stableReactRouterContext/stableReactRouterContext';
import { useAppStore, useLoginStore, useParentalPinStore, useRootStore } from 'hooks';
import routes from 'routesPaths';
import { globalStorage } from 'services/globalStorage';
import localStorageService from 'services/localStorageService';

import { resetSSOCookies } from '../utils';

import styles from './SettingsMenu.module.scss';

interface Props {
  settingsStore: PageStore;
}

function SettingsMenu({ settingsStore }: Props) {
  const domain =
    appStore?.params?.dmsSetting?.endpoint === ENV_ENDPOINTS.STAGING ? o2Domains.staging : o2Domains.production;

  const modalContext = useModalContext();
  const { logoutUser } = useLoginStore();
  const rootStore = useRootStore();

  const {
    appStore: {
      dms: { providers },
    },
  } = useAppStore();
  const parentalControlStore = useParentalPinStore();
  const { navigate } = useStableReactRouter();
  const intl = useIntl();
  const logOutButtonLabel = intl.formatMessage({ defaultMessage: 'logout', id: 'Settings.SettingsMenu.buttonLogout' });

  function handleSettingsLinkClick(substore: SubpageStore) {
    return (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      const isActiveLink = e.currentTarget.className.includes('active');

      if (isActiveLink) {
        return;
      }

      if (parentalControlStore.isBlockByParentRating(substore)) {
        const callback = (success: boolean) => {
          if (success) {
            navigate(`${routes.settings.path}${substore.link}`);
          }
        };

        e.preventDefault();

        const validateParental = (pin: string) =>
          parentalControlStore.validatePin(pin, ParentalPinType.parental, substore);

        modalContext.openModal(
          <PinCodeModal
            callback={callback}
            type={PinCodeModalTypes.EnterParental}
            context={UIDAPresentationTypeEnum.settings}
            validatePin={validateParental}
          />,
        );
      }
    };
  }

  const handleLogout = async (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();

    async function onAccept() {
      globalStorage.setIsLogOutProcessBySettings(true);
      modalContext.closeModal();

      Object.keys(providers).forEach((provider) => {
        localStorageService.removeItem(`${provider}${DO_NOT_SHOW_AGAIN}`);
      });

      await logoutUser();

      resetSSOCookies(domain);
      navigate(routes.login.path, { replace: true });

      globalStorage.setIsLogOutProcessBySettings(false);
    }

    modalContext.openModal(
      <DefaultModal
        buttonProps={{
          onAccept,
          acceptButtonText: intl.formatMessage({
            defaultMessage: 'Logout',
            id: 'Settings.SettingsMenu.DefaultModal.buttonLogout',
          }),
        }}
        title={intl.formatMessage({ defaultMessage: 'Logout', id: 'Settings.SettingsMenu.DefaultModal.title' })}
      >
        <p>
          {intl.formatMessage({ defaultMessage: 'Are you sure you want to logout?', id: 'Settings.SettingsMenu.textLogout' })}
        </p>
      </DefaultModal>,
    );
  };

  return (
    <nav className={styles.wrapper}>
      <ul className={styles.menu}>
        {settingsStore.children.map((substore) => {
          const { link, name, image } = substore as SubpageStore;
          const isFavorites = substore.subtype === UIDAPresentationTypeEnum.favorites;
          const isPersonalLineup = substore.subtype === UIDAPresentationTypeEnum.favorites;

          const isChannelPersonalizationCategory = isFavorites || isPersonalLineup;

          if (rootStore.isChannelPersonalizationDisabled && isChannelPersonalizationCategory) {
            return null;
          }

          return (
            <li key={link}>
              <NavLink
                aria-label={name}
                tabIndex={0}
                className={({ isActive }) => (isActive ? ' active' : '')}
                data-type={isFavorites ? FAVORITES : ''}
                onClick={handleSettingsLinkClick(substore as SubpageStore)}
                to={`${routes.settings.path}${link}`}
              >
                <p className={styles.textItem}>
                  {image && <img aria-hidden alt={name} src={image + IMAGE_PARAM} />}
                  {name}
                </p>
              </NavLink>
            </li>
          );
        })}

        <li>
          <NavLink
            tabIndex={0}
            className={({ isActive }) => (isActive ? ' active' : '')}
            onClick={handleLogout}
            role='link'
            aria-label={logOutButtonLabel}
            to={routes.home.path}
          >
            <p className={styles.textItemLogOut} aria-hidden="true">
              {logOutButtonLabel}
            </p>
          </NavLink>
        </li>
      </ul>
    </nav>
  );
}

export default observer(SettingsMenu);
